import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UserRole } from '@/shared/models/user';
import { AuthService } from '@/shared/services/auth.service';
import { AccountStatus } from '@/shared/models/company';
import { CompanyTierSettingService } from '@/shared/services/company-tier-setting.service';
import { ShippingCarrierService } from '@/shared/services/shipping-carrier.service';
import { ToastService } from '@/shared/services/toast.service';
import { LogoComponent } from '@/shared/components/logo/logo.component';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { NgStyle } from '@angular/common';
import { LoadingDirective } from '@/shared/directives/loading.directive';
import { TranslatePipe } from '@ngx-translate/core';

export interface MenuItem {
  title: string;
  link?: string;
  icon?: string;
  submenu?: { title: string; link: string }[];
  expanded?: boolean;
  roles: UserRole[];
}

const userRoles = [UserRole.admin, UserRole.employee];
// CRAT-975 — the only two nav entries a Lister sees: Inventory (their work area; the /inventory
// link redirects them to the search-products landing) and Settings (user settings only).
const inventoryRoles = [...userRoles, UserRole.lister];

let allMenuItems: MenuItem[] = [
  {
    title: 'navbar.getStarted',
    link: '/get-started',
    icon: '/assets/icons/success-crate.svg',
    roles: [UserRole.admin],
  },
  {
    title: 'navbar.dashboard',
    link: '/dashboard',
    icon: '/assets/icons/globe.svg',
    roles: userRoles,
  },
  {
    title: 'navbar.inventory',
    link: '/inventory',
    icon: '/assets/icons/cratebase.svg',
    roles: inventoryRoles,
  },
  {
    title: 'navbar.orders',
    link: '/orders',
    roles: userRoles,
    icon: '/assets/icons/orders-crate.svg',
  },
  {
    title: 'navbar.picking',
    link: '/picking',
    icon: '/assets/icons/picking.svg',
    roles: userRoles,
  },
  {
    title: 'navbar.packing',
    link: '/packing',
    icon: '/assets/icons/packing.svg',
    roles: userRoles,
  },
  {
    title: 'navbar.pickup',
    link: '/pickup',
    icon: '/assets/icons/pickup.svg',
    roles: userRoles,
  },
  {
    title: 'navbar.waybills',
    link: '/waybills',
    icon: '/assets/icons/waybills.svg',
    roles: userRoles,
  },
  {
    title: 'navbar.shipments',
    link: '/shipments',
    icon: '/assets/icons/shipments.svg',
    roles: userRoles,
  },
  {
    title: 'navbar.archive',
    link: '/archive',
    icon: '/assets/icons/archive.svg',
    roles: userRoles,
  },
  {
    title: 'navbar.customers',
    link: '/customers',
    icon: '/assets/icons/user.svg',
    roles: [UserRole.admin],
  },
  {
    title: 'navbar.settings',
    icon: '/assets/icons/settings.svg',
    link: '/settings',
    roles: inventoryRoles,
  },
  {
    title: 'navbar.companies',
    icon: '/assets/icons/globe.svg',
    link: '/admin/companies',
    roles: [UserRole.superAdmin],
  },
  {
    title: 'navbar.companyTierSetting',
    icon: '/assets/icons/globe.svg',
    link: '/admin/company-tier-setting',
    roles: [UserRole.superAdmin],
  },
  {
    title: 'navbar.billingLevels',
    icon: '/assets/icons/globe.svg',
    link: '/admin/billing-levels',
    roles: [UserRole.superAdmin],
  },
  {
    title: 'navbar.shippingCarrierCredentials',
    icon: '/assets/images/credentials-icon.png',
    link: '/admin/shipping-carrier-credentials',
    roles: [UserRole.superAdmin],
  },
  {
    title: 'navbar.carrierInvoiceImports',
    icon: '/assets/images/credentials-icon.png',
    link: '/admin/carrier-invoice-imports',
    roles: [UserRole.superAdmin],
  },
  {
    title: 'navbar.carrierInvoiceReconciliation',
    icon: '/assets/images/credentials-icon.png',
    link: '/admin/carrier-invoice-reconciliation',
    roles: [UserRole.superAdmin],
  },
  {
    title: 'navbar.shippingInvoices',
    icon: '/assets/images/credentials-icon.png',
    link: '/admin/shipping-invoices',
    roles: [UserRole.superAdmin],
  },
  {
    title: 'navbar.announcements',
    icon: '/assets/icons/message-square.svg',
    link: '/admin/announcements',
    roles: [UserRole.superAdmin],
  },
  {
    title: 'navbar.systemMonitoring',
    icon: '/assets/icons/settings.svg',
    link: '/admin/system-monitoring',
    roles: [UserRole.superAdmin],
  },
  {
    title: 'navbar.generatedLabels',
    icon: '/assets/icons/shipments.svg',
    link: '/admin/generated-labels',
    roles: [UserRole.superAdmin],
  },
  {
    title: 'navbar.qrRedirects',
    icon: '/assets/icons/settings.svg',
    link: '/admin/qr-redirects',
    roles: [UserRole.superAdmin],
  },
];

@UntilDestroy()
@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  imports: [
    LogoComponent,
    MatButtonModule,
    RouterLink,
    RouterLinkActive,
    MatTooltip,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    NgStyle,
    LoadingDirective,
    TranslatePipe,
  ],
})
export class SidenavComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private authService = inject(AuthService);
  private toast = inject(ToastService);
  private shipEngine = inject(ShippingCarrierService);
  private companyTierSettingService = inject(CompanyTierSettingService);

  @Input() isMobile = false;
  @Input() isCondensed = false;

  @Output() menuToggle: EventEmitter<void> = new EventEmitter<void>();
  @Output() logout: EventEmitter<void> = new EventEmitter<void>();

  activeLink: string;
  isLoggingOut = false;
  isPNEnabled = true;
  isBulkPickingEnabled: boolean = false;
  isInventoryEnabled: boolean = false;
  isLocalPickupScreenEnabled: boolean = false;
  isCustomerCrmEnabled: boolean = false;

  menuItems: MenuItem[] = [];

  async ngOnInit() {
    // Subscribe to user changes to update navigation when user data is refreshed
    this.authService.user$.pipe(untilDestroyed(this)).subscribe(async (user) => {
      if (user && user.role !== UserRole.superAdmin) {
        try {
          const [shippingCarriers, tierSettings] = await Promise.all([
            this.shipEngine.getMyShippingCarriers(),
            this.companyTierSettingService.getCompanyTier(user.companyId),
          ]);
          this.isPNEnabled = shippingCarriers.some((item) => item.type === 'postNord' && item.status === 'enabled');
          this.isBulkPickingEnabled = tierSettings?.bulkPickingEnabled ?? false;
          this.isInventoryEnabled = tierSettings?.inventoryEnabled ?? false;
          this.isLocalPickupScreenEnabled = tierSettings?.localPickupScreenEnabled ?? false;
          this.isCustomerCrmEnabled = tierSettings?.customerCrmEnabled ?? false;
        } catch (error) {
          console.error('Error fetching user-specific settings:', error);
        }
      }
      // Update menu items whenever user data changes
      this.menuItems = this.getMenuItems();
    });

    this.activeLink = this.router.url.split('?')[0];
    this.router.events.pipe(untilDestroyed(this)).subscribe(async (event) => {
      if (event instanceof NavigationEnd) {
        this.activeLink = event.url.split('?')[0];

        // Check for refresh requirements on navigation (webhook-triggered changes)
        try {
          await this.authService.checkForRefreshRequirements();
        } catch (error) {
          console.warn('Failed to check refresh requirements on navigation:', error);
        }
      }
    });
    this.setExpanded();
  }

  getMenuItems() {
    if (!this.authService.user) return [];

    let filteredItems = [...allMenuItems];

    // Apply feature-based filtering
    if (!this.isPNEnabled) filteredItems = filteredItems.filter((item) => item.title != 'navbar.waybills');
    if (!this.isBulkPickingEnabled)
      filteredItems = filteredItems.filter((item) => item.title != 'navbar.picking' && item.title != 'navbar.packing');
    if (!this.isInventoryEnabled) filteredItems = filteredItems.filter((item) => item.title != 'navbar.inventory');
    if (!this.isLocalPickupScreenEnabled) filteredItems = filteredItems.filter((item) => item.title != 'navbar.pickup');
    if (!this.isCustomerCrmEnabled) filteredItems = filteredItems.filter((item) => item.title != 'navbar.customers');

    // Apply account status filtering
    const accountStatus = this.authService.user.company?.accountStatus;
    const isAdmin = this.authService.user.role === UserRole.admin;

    if (isAdmin && accountStatus === AccountStatus.Inactive) {
      // For Inactive status: Only show Get Started tab for ADMIN users
      filteredItems = filteredItems.filter((item) => item.title === 'navbar.getStarted');
    } else if (isAdmin && accountStatus === AccountStatus.Pending) {
      // For Pending status: Show all tabs + Get Started tab for ADMIN users (Get Started already included)
      // No additional filtering needed - keep all tabs including Get Started
    } else if (accountStatus === AccountStatus.Active || !accountStatus || !isAdmin) {
      // For Active status, undefined status, or non-admin users: Remove Get Started tab
      filteredItems = filteredItems.filter((item) => item.title !== 'navbar.getStarted');
    }

    const finalItems = filteredItems.filter((item) => item.roles.includes(this.authService.user.role));
    return finalItems;
  }

  get condensed() {
    return this.isMobile ? false : this.isCondensed;
  }

  setExpanded() {
    this.menuItems
      .filter((item) => item.submenu != null)
      .forEach((item) => {
        item.expanded = item.submenu.map((i) => i.link).includes(this.activeLink);
      });
  }

  itemClick(item: MenuItem) {
    if (item.submenu && !this.condensed) {
      item.expanded = !item.expanded;
    }
  }
  async onLogout() {
    try {
      this.isLoggingOut = true;
      await this.authService.logout(this.authService.user.email);
      this.logout.emit();
    } catch (e) {
      this.toast.showError(e, false, '', true);
    } finally {
      this.isLoggingOut = false;
    }
  }

  ngOnDestroy() {
    this.toast.onDismiss();
  }
}
